import dayjs from "dayjs"
import { useLocalStorage } from "usehooks-ts"

import type { TrackingTechnologyConsentResponse } from "src/types"
import { GlobalLocalStorageKey } from "src/types"

// serialize TrackingTechnologyConsentResponse to a string for local storage
export function serializer(data: TrackingTechnologyConsentResponse | undefined): string {
  return JSON.stringify(data)
}

// this calculates the expiration date as 6 months from the response
export function getExpiration(dateString: string | undefined): string {
  return dayjs(dateString)
    .add(
      6,
      "month",
    )
    .toISOString()
}

// deserialize string from local storage to TrackingTechnologyConsentResponse and validate it
export function deserializer(localStorageValue: string): TrackingTechnologyConsentResponse | undefined {
  try {
    const json = JSON.parse(localStorageValue) as null | string | boolean | number | object

    if (json === null || typeof json !== "object") {
      throw new Error("invalid stored response")
    }

    const data: Partial<TrackingTechnologyConsentResponse> = json

    // enforce that the value is either "accepted" or "rejected"
    if (data.value !== "accepted" && data.value !== "rejected") {
      throw new Error("invalid response value")
    }

    // enforce that the updatedAt is defined and a valid date
    const date = data.updatedAt && dayjs(data.updatedAt)
    if (!date || !date.isValid()) {
      throw new Error("invalid updatedAt")
    }

    // require responses to not be expired
    const expiration = getExpiration(data.updatedAt)
    if (dayjs().isAfter(expiration)) {
      throw new Error("expired response")
    }

    return data as TrackingTechnologyConsentResponse
  } catch {
    return undefined
  }
}

// https://usehooks-ts.com/react-hook/use-local-storage
export function useTrackingTechnologyConsent() {
  return useLocalStorage<TrackingTechnologyConsentResponse | undefined>(
    GlobalLocalStorageKey.TrackingTechnologyConsentResponse,
    undefined,
    {
      serializer,
      deserializer,
    },
  )
}
